import { Effect } from 'effect';
import type { LookDTOType } from '../schemas/styling';

type PollVisualSearchOptions = {
  lookId: number;
  fetchLookById: (lookId: number) => Promise<LookDTOType>;
  onLookUpdated?: (look: LookDTOType) => void;
};

type PollVisualSearchResult = {
  status: 'ready' | 'failed';
  look: LookDTOType;
};

const getSourceItems = (look: LookDTOType) => (Array.isArray(look.visual_search) ? look.visual_search : []);

type WidgetsPayload = {
  status?: string;
  build_error?: string;
};

const getWidgetsPayload = (look: LookDTOType): WidgetsPayload =>
  look.widgets && typeof look.widgets === 'object' && !Array.isArray(look.widgets)
    ? {
        status: typeof look.widgets.status === 'string' ? look.widgets.status : undefined,
        build_error:
          typeof look.widgets.build_error === 'string' ? look.widgets.build_error : undefined,
      }
    : {};

const hasGeneratedSourceImages = (look: LookDTOType) =>
  getSourceItems(look).some(item => {
    if (!item || typeof item !== 'object') {
      return false;
    }

    const isolatedImageUrl =
      'isolated_image_url' in item && typeof item.isolated_image_url === 'string'
        ? item.isolated_image_url.trim()
        : '';

    return Boolean(isolatedImageUrl);
  });

const hasVisualSearchFailed = (look: LookDTOType) => {
  const payload = getWidgetsPayload(look);
  const status = typeof payload.status === 'string' ? payload.status : '';
  const buildError = typeof payload.build_error === 'string' ? payload.build_error.trim() : '';

  return status === 'failed' || Boolean(buildError);
};

const INITIAL_POLL_INTERVAL_MS = 10000;
const POLL_INTERVAL_STEP_MS = 5000;

const getPollDelayMs = (attempt: number) =>
  INITIAL_POLL_INTERVAL_MS + attempt * POLL_INTERVAL_STEP_MS;

const createPollEffect = ({ lookId, fetchLookById, onLookUpdated }: PollVisualSearchOptions) => {
  const poll = (attempt: number): Effect.Effect<PollVisualSearchResult, never, never> =>
    Effect.sleep(getPollDelayMs(attempt)).pipe(
      Effect.zipRight(
        Effect.tryPromise({
          try: () => fetchLookById(lookId),
          catch: error => error,
        }).pipe(
          Effect.matchEffect({
            onFailure: error =>
              Effect.sync(() => {
                console.error('Error polling visual-search look state:', error);
              }).pipe(Effect.zipRight(poll(attempt + 1))),
            onSuccess: nextLook =>
              Effect.sync(() => {
                onLookUpdated?.(nextLook);
                return nextLook;
              }).pipe(
                Effect.flatMap(look => {
                  if (hasGeneratedSourceImages(look)) {
                    return Effect.succeed({ status: 'ready', look } as const);
                  }

                  if (hasVisualSearchFailed(look)) {
                    return Effect.succeed({ status: 'failed', look } as const);
                  }

                  return poll(attempt + 1);
                }),
              ),
          }),
        ),
      ),
    );

  return poll(0);
};

export const pollLookUntilVisualSearchReady = async ({
  lookId,
  fetchLookById,
  onLookUpdated,
}: PollVisualSearchOptions): Promise<PollVisualSearchResult> => {
  return Effect.runPromise(createPollEffect({ lookId, fetchLookById, onLookUpdated }));
};
